import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  LinearProgress,
  Box,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

interface MessageDeletionTimerDialogProps {
  open: boolean;
  onClose: () => void;
  deletionTimeMs?: number; // Default: 30 minutes
}

const MessageDeletionTimerDialog: React.FC<MessageDeletionTimerDialogProps> = ({
  open,
  onClose,
  deletionTimeMs = 30 * 60 * 1000, // 30 minutes in milliseconds
}) => {
  const [remainingTimeMs, setRemainingTimeMs] = useState(deletionTimeMs);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!open) {
      setRemainingTimeMs(deletionTimeMs);
      setIsVisible(true);
      return;
    }

    const interval = setInterval(() => {
      setRemainingTimeMs((prev) => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          setIsVisible(false);
          onClose();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, deletionTimeMs, onClose]);

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const progress = isVisible
    ? (deletionTimeMs - remainingTimeMs) / deletionTimeMs
    : 1;

  if (!isVisible) {
    return null;
  }

  return (
    <Dialog
      open={open && isVisible}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: '#4CAF50', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
            Message sent successfully!
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
            }}
          >
            This message will be deleted from the server in:
          </Typography>

          {/* Timer display */}
          <Typography
            variant="h4"
            sx={{
              color: '#2196F3',
              fontWeight: 'bold',
              fontFamily: 'monospace',
              letterSpacing: 2,
            }}
          >
            {formatTime(remainingTimeMs)}
          </Typography>

          {/* Progress indicator */}
          <LinearProgress
            variant="determinate"
            value={progress * 100}
            sx={{
              width: '100%',
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#2196F3',
                borderRadius: 3,
              },
            }}
          />

          <Button
            variant="contained"
            onClick={onClose}
            fullWidth
            sx={{
              mt: 1,
              backgroundColor: '#2196F3',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#1976D2',
              },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MessageDeletionTimerDialog;

